// Dados pré-definidos para o sistema SOS - Médico Homeopata Virtual

export interface SOSOption {
  id: string;
  label: string;
  description?: string;
}

// Grupos pré-definidos do sistema SOS
export interface SOSGroup {
  id: string;
  title: string;
  placeholder: string;
  required: boolean;
}

// Doença/Enfermidade com suas opções para cada grupo
export interface SOSDisease {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  groups: {
    [groupId: string]: SOSOption[];
  };
  selectionLimits?: {
    [groupId: string]: number;
  };
  knowledge_base_file?: string; // Nome do arquivo no Storage
  knowledgeBaseFile?: File; // Arquivo temporário para upload
}

// Grupos pré-definidos do sistema (não podem ser alterados pelo admin)
export const sosGroups: SOSGroup[] = [
  {
    id: 'tipo_caracteristica',
    title: 'Tipo e Característica',
    placeholder: 'Selecione o tipo e característica',
    required: true,
  },
  {
    id: 'piora_com',
    title: 'Piora com',
    placeholder: 'Selecione o que piora os sintomas',
    required: true,
  },
  {
    id: 'melhora_com',
    title: 'Melhora com',
    placeholder: 'Selecione o que melhora os sintomas',
    required: true,
  },
  {
    id: 'causas_sensacoes',
    title: 'Causas e Sensações Desencadeantes',
    placeholder: 'Selecione as causas e sensações',
    required: true,
  },
  {
    id: 'sintomas_concomitantes',
    title: 'Sintomas Concomitantes',
    placeholder: 'Selecione os sintomas concomitantes',
    required: true,
  },
  {
    id: 'comportamentos_reacoes',
    title: 'Comportamentos e Reações Físicas',
    placeholder: 'Selecione os comportamentos e reações',
    required: true,
  },
  {
    id: 'temporalidade_ritmo',
    title: 'Temporalidade e Ritmo',
    placeholder: 'Selecione a temporalidade e ritmo',
    required: true,
  }
];

// Interface para compatibilidade com o sistema atual
export interface SOSFieldData {
  id: string;
  title: string;
  placeholder: string;
  options: SOSOption[];
  required: boolean;
}

// Doenças/Enfermidades pré-cadastradas no sistema
export const sosDiseases: SOSDisease[] = [
  {
    id: 'febre',
    name: 'Febre',
    description: 'Elevação da temperatura corporal',
    active: true,
    groups: {
      tipo_caracteristica: [
        { id: 'agudo', label: 'Agudo', description: 'Início súbito e intenso' },
        { id: 'cronico', label: 'Crônico', description: 'Persistente e duradouro' },
        { id: 'intermitente', label: 'Intermitente', description: 'Vai e vem periodicamente' },
        { id: 'alta', label: 'Alta (>39°C)', description: 'Temperatura muito elevada' },
        { id: 'moderada', label: 'Moderada (38-39°C)', description: 'Temperatura moderadamente elevada' },
        { id: 'baixa', label: 'Baixa (37-38°C)', description: 'Temperatura ligeiramente elevada' }
      ],
      piora_com: [
        { id: 'movimento', label: 'Movimento', description: 'Piora com atividade física' },
        { id: 'frio', label: 'Frio', description: 'Piora com temperaturas baixas' },
        { id: 'noite', label: 'À Noite', description: 'Piora no período noturno' },
        { id: 'descobrir', label: 'Descobrir-se', description: 'Piora ao tirar cobertores' }
      ],
      melhora_com: [
        { id: 'repouso', label: 'Repouso', description: 'Melhora com descanso' },
        { id: 'calor', label: 'Calor', description: 'Melhora com aquecimento' },
        { id: 'cobrir', label: 'Cobrir-se', description: 'Melhora com cobertores' },
        { id: 'sono', label: 'Sono', description: 'Melhora após dormir' }
      ],
      causas_sensacoes: [
        { id: 'infeccioso', label: 'Infeccioso', description: 'Exposição a infecções' },
        { id: 'climatico', label: 'Climático', description: 'Mudanças climáticas' },
        { id: 'emocional', label: 'Emocional', description: 'Causas emocionais (raiva, tristeza, susto)' },
        { id: 'exposicao_sol', label: 'Exposição ao Sol', description: 'Exposição solar excessiva' }
      ],
      sintomas_concomitantes: [
        { id: 'calafrios', label: 'Calafrios', description: 'Sensação de frio' },
        { id: 'sudorese', label: 'Sudorese', description: 'Suor excessivo' },
        { id: 'dor_cabeca', label: 'Dor de Cabeça', description: 'Cefaleia' },
        { id: 'fraqueza', label: 'Fraqueza', description: 'Sensação de fraqueza' },
        { id: 'sede', label: 'Sede', description: 'Sede excessiva' }
      ],
      comportamentos_reacoes: [
        { id: 'inquieto', label: 'Inquieto', description: 'Não consegue ficar parado' },
        { id: 'prostrado', label: 'Prostrado', description: 'Quer ficar deitado' },
        { id: 'irritavel', label: 'Irritável', description: 'Facilmente irritado' },
        { id: 'busca_companhia', label: 'Busca Companhia', description: 'Quer estar com pessoas' }
      ],
      temporalidade_ritmo: [
        { id: 'noite', label: 'Noite', description: 'Sintomas à noite' },
        { id: 'periodico', label: 'Periódico', description: 'Aparece em períodos regulares' },
        { id: 'subitamente', label: 'Subitamente', description: 'Aparece de repente' },
        { id: 'crescente', label: 'Crescente', description: 'Intensidade crescente' }
      ]
    }
  },
  {
    id: 'dor_cabeca',
    name: 'Dor de Cabeça',
    description: 'Cefaleia ou enxaqueca',
    active: true,
    groups: {
      tipo_caracteristica: [
        { id: 'pulsatil', label: 'Pulsátil', description: 'Sensação de pulsação' },
        { id: 'pressao', label: 'Pressão', description: 'Sensação de peso ou aperto' },
        { id: 'pontada', label: 'Pontada', description: 'Dor em forma de pontadas' },
        { id: 'latejante', label: 'Latejante', description: 'Dor que pulsa' },
        { id: 'queimacao', label: 'Queimação', description: 'Sensação de ardor' },
        { id: 'surdo', label: 'Surdo', description: 'Dor constante e profunda' }
      ],
      piora_com: [
        { id: 'movimento', label: 'Movimento', description: 'Piora com atividade física' },
        { id: 'luz', label: 'Luz', description: 'Piora com luz forte' },
        { id: 'ruido', label: 'Ruído', description: 'Piora com barulho' },
        { id: 'estresse', label: 'Estresse', description: 'Piora com tensão emocional' },
        { id: 'jejum', label: 'Jejum', description: 'Piora em jejum' }
      ],
      melhora_com: [
        { id: 'repouso', label: 'Repouso', description: 'Melhora com descanso' },
        { id: 'escuro', label: 'Escuro', description: 'Melhora no escuro' },
        { id: 'pressao', label: 'Pressão', description: 'Melhora com pressão local' },
        { id: 'frio', label: 'Frio', description: 'Melhora com resfriamento' },
        { id: 'sono', label: 'Sono', description: 'Melhora após dormir' }
      ],
      causas_sensacoes: [
        { id: 'emocional', label: 'Emocional', description: 'Causas emocionais (raiva, tristeza, susto)' },
        { id: 'alimentar', label: 'Alimentar', description: 'Relacionado à alimentação' },
        { id: 'hormonal', label: 'Hormonal', description: 'Alterações hormonais' },
        { id: 'falta_sono', label: 'Falta de Sono', description: 'Privação de sono' },
        { id: 'excesso_trabalho', label: 'Excesso de Trabalho', description: 'Sobrecarga de trabalho' }
      ],
      sintomas_concomitantes: [
        { id: 'nausea', label: 'Náusea', description: 'Enjoo' },
        { id: 'vomito', label: 'Vômito', description: 'Vômitos' },
        { id: 'tontura', label: 'Tontura', description: 'Vertigem ou desequilíbrio' },
        { id: 'irritabilidade', label: 'Irritabilidade', description: 'Estado de irritação' },
        { id: 'sensibilidade_luz', label: 'Sensibilidade à Luz', description: 'Fotofobia' }
      ],
      comportamentos_reacoes: [
        { id: 'inquieto', label: 'Inquieto', description: 'Não consegue ficar parado' },
        { id: 'quer_solidao', label: 'Quer Solidão', description: 'Prefere ficar sozinho' },
        { id: 'irritavel', label: 'Irritável', description: 'Facilmente irritado' },
        { id: 'geme', label: 'Geme', description: 'Emite gemidos' }
      ],
      temporalidade_ritmo: [
        { id: 'manha', label: 'Manhã', description: 'Sintomas pela manhã' },
        { id: 'tarde', label: 'Tarde', description: 'Sintomas à tarde' },
        { id: 'periodico', label: 'Periódico', description: 'Aparece em períodos regulares' },
        { id: 'gradualmente', label: 'Gradualmente', description: 'Aparece aos poucos' }
      ]
    }
  }
];

// Manter compatibilidade com o sistema atual
export const sosFieldsData: SOSFieldData[] = [
  {
    id: 'enfermidade_doenca',
    title: 'Enfermidade/Doença',
    placeholder: 'Selecione a enfermidade principal',
    required: true,
    options: sosDiseases.filter(d => d.active).map(d => ({
      id: d.id,
      label: d.name,
      description: d.description
    }))
  },
  ...sosGroups.map(group => ({
    id: group.id,
    title: group.title,
    placeholder: group.placeholder,
    required: group.required,
    options: [] // Será preenchido dinamicamente baseado na doença selecionada
  }))
];